(function () {
  const ready = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }
    callback();
  };

  ready(() => {
    const root = document.querySelector(".anou-soft-track-b");
    if (!root) return;

    setupMiniQuiz(root);
    setupFormulaPage(root);
    setupMenus(root);
    setupMobileTopbar(root);
    setupStandardHeader(root);
    setupStoryCarousel(root);
    setupHorizontalRails(root);
    setupVibes(root);
    setupGallery(root);
    setupPurchaseChoices(root);
    setupStickyPdp(root);
    setupUsage(root);
    setupParityVideos(root);
    setupCartForms(root);
    setupDiscountCode(root);
    setupScrollSignal(root);
    setupReadSectionNavigation(root);
  });

  function setupMiniQuiz(root) {
    root.querySelectorAll("[data-anou-mini-quiz]").forEach((quiz) => {
      const start = quiz.querySelector("[data-anou-mini-quiz-start]");
      const mobileOpen = root.querySelector("[data-anou-mobile-formula-open]");
      const mobileWhatsapp = root.querySelector('.soft-mobile-whatsapp-launcher[data-mobile-action="formula"]');
      const step = quiz.querySelector("[data-anou-mini-quiz-step]");
      const results = Array.from(quiz.querySelectorAll("[data-anou-mini-quiz-result]"));
      const setOpen = (open) => {
        quiz.classList.toggle("is-started", open);
        quiz.classList.toggle("is-mobile-open", open);
        quiz.setAttribute("data-mobile-open", String(open));
        start?.setAttribute("aria-expanded", String(open));
        mobileOpen?.setAttribute("aria-expanded", String(open));
        mobileOpen?.setAttribute("data-mobile-open", String(open));
        mobileWhatsapp?.setAttribute("data-mobile-open", String(open));
      };
      const reset = () => {
        setOpen(false);
        if (step) step.hidden = false;
        results.forEach((result) => { result.hidden = true; });
      };

      start?.addEventListener("click", () => {
        setOpen(true);
      });
      mobileOpen?.addEventListener("click", () => {
        if (step) step.hidden = false;
        results.forEach((result) => { result.hidden = true; });
        setOpen(true);
      });
      quiz.querySelector("[data-anou-mini-quiz-close]")?.addEventListener("click", reset);
      quiz.querySelectorAll("[data-anou-mini-quiz-choice]").forEach((choice) => {
        choice.addEventListener("click", () => {
          const slug = choice.getAttribute("data-anou-mini-quiz-choice");
          if (step) step.hidden = true;
          results.forEach((result) => {
            result.hidden = result.getAttribute("data-anou-mini-quiz-result") !== slug;
          });
        });
      });
    });
  }

  function setupFormulaPage(root) {
    const experience = root.querySelector("[data-anou-formula-page]");
    if (!experience) return;
    const concerns = new Set();
    let intent = "";
    let dayPart = "";
    let activeStep = 1;
    const steps = Array.from(experience.querySelectorAll("[data-anou-formula-step]"));
    const result = experience.querySelector("[data-anou-formula-result]");
    const intro = experience.querySelector("[data-anou-formula-intro]");
    const back = experience.querySelector("[data-anou-formula-back]");
    const progressLabel = experience.querySelector("[data-anou-formula-progress-label]");
    const progressBar = experience.querySelector("[data-anou-formula-progress-bar]");
    const resultCopy = {
      aroDay: {
        headline: experience.dataset.resultAroDayHeadline || "Begin with Arō.",
        rationale: experience.dataset.resultAroDayRationale || "Arō supports hydration retention and barrier comfort through the day — the right first step for reactive or dry skin.",
      },
      aroDayPigmentation: {
        headline: experience.dataset.resultAroDayPigmentationHeadline || "Begin with Arō.",
        rationale: experience.dataset.resultAroDayPigmentationRationale || "A calm daytime moisturiser keeps your barrier steady while you decide when to add a nighttime treatment.",
      },
      aroNight: {
        headline: experience.dataset.resultAroNightHeadline || "Night comfort starts with Arō.",
        rationale: experience.dataset.resultAroNightRationale || "When redness or dryness is the main concern, a barrier-supporting moisturiser at night often outperforms a treatment step on its own.",
      },
      dali: {
        headline: experience.dataset.resultDaliHeadline || "Begin with Dalí.",
        rationale: experience.dataset.resultDaliRationale || "A nighttime treatment built around clearer skin tone and post-acne marks — introduced slowly, a few nights a week.",
      },
      duo: {
        headline: experience.dataset.resultDuoHeadline || "Start with the Duo.",
        rationale: experience.dataset.resultDuoRationale || "Arō for daily comfort and Dalí for skin tone at night — the simplest way to address both barrier care and pigmentation without overloading your shelf.",
      },
    };

    const showStep = (step) => {
      activeStep = step;
      experience.classList.remove("is-result");
      if (result) result.hidden = true;
      if (intro) intro.hidden = false;
      steps.forEach((panel) => { panel.hidden = Number(panel.getAttribute("data-anou-formula-step")) !== step; });
      if (back) back.hidden = step === 1;
      if (progressLabel) progressLabel.textContent = `Question ${step} of 3`;
      if (progressBar) progressBar.style.width = `${(step / 3) * 100}%`;
    };
    const primaryConcern = () => {
      for (const concern of ["post-acne", "pigmentation", "acne", "redness", "dryness"]) {
        if (concerns.has(concern)) return concern;
      }
      return "dryness";
    };
    const showResult = () => {
      const concern = primaryConcern();
      let slug = "aro";
      let headline = resultCopy.aroDay.headline;
      let rationale = resultCopy.aroDay.rationale;

      if (intent === "routine") {
        slug = "duo";
        headline = resultCopy.duo.headline;
        rationale = resultCopy.duo.rationale;
      } else if (dayPart === "day") {
        if (["pigmentation", "post-acne", "acne"].includes(concern)) {
          headline = resultCopy.aroDayPigmentation.headline;
          rationale = resultCopy.aroDayPigmentation.rationale;
        }
      } else if (["dryness", "redness"].includes(concern)) {
        headline = resultCopy.aroNight.headline;
        rationale = resultCopy.aroNight.rationale;
      } else {
        slug = "dali";
        headline = resultCopy.dali.headline;
        rationale = resultCopy.dali.rationale;
      }

      const productName = slug === "duo" ? "Nourish and Treat Duo" : slug === "dali" ? "Dalí" : "Arō";
      const href = slug === "duo" ? "/products/buy-bundle" : `/products/${slug}`;
      experience.classList.add("is-result");
      if (intro) intro.hidden = true;
      steps.forEach((panel) => { panel.hidden = true; });
      if (back) back.hidden = true;
      if (result) {
        result.hidden = false;
        result.className = `soft-formula-result is-${slug}`;
      }
      if (progressLabel) progressLabel.textContent = "Your result";
      if (progressBar) progressBar.style.width = "100%";
      experience.querySelector("[data-anou-formula-result-headline]").textContent = headline;
      experience.querySelector("[data-anou-formula-result-rationale]").textContent = rationale;
      const image = experience.querySelector("[data-anou-formula-result-image]");
      image.src = experience.dataset[`${slug}Image`];
      image.alt = productName;
      const media = experience.querySelector("[data-anou-formula-result-media]");
      media.className = `soft-product-media is-result-product is-${slug}`;
      const overlay = experience.querySelector("[data-anou-formula-result-overlay]");
      overlay.href = href;
      overlay.setAttribute("aria-label", `Open ${productName} product page`);
      experience.querySelector("[data-anou-formula-result-variant]").value = experience.dataset[`${slug}Variant`];
      experience.querySelector("[data-anou-formula-result-price]").textContent = experience.dataset[`${slug}Price`];
      const compare = experience.querySelector("[data-anou-formula-result-compare]");
      compare.textContent = experience.dataset[`${slug}Compare`] || "";
      compare.hidden = !compare.textContent;
      const cta = experience.querySelector("[data-anou-formula-result-cta]");
      cta.className = `soft-pill-cta is-${slug}${slug === "duo" ? " soft-duo-buy-cta" : ""}`;
      const link = experience.querySelector("[data-anou-formula-result-link]");
      link.href = href;
      link.childNodes[0].textContent = slug === "duo" ? "View Duo" : `View ${productName}`;
    };
    const reset = () => {
      concerns.clear();
      intent = "";
      dayPart = "";
      experience.querySelectorAll("[data-anou-formula-concern], [data-anou-formula-intent], [data-anou-formula-daypart]").forEach((button) => {
        button.classList.remove("is-selected");
        button.setAttribute("aria-pressed", "false");
      });
      experience.querySelectorAll("[data-anou-formula-continue]").forEach((button) => { button.disabled = true; });
      showStep(1);
    };

    experience.querySelectorAll("[data-anou-formula-concern]").forEach((button) => {
      button.addEventListener("click", () => {
        const value = button.getAttribute("data-anou-formula-concern");
        concerns.has(value) ? concerns.delete(value) : concerns.add(value);
        button.classList.toggle("is-selected", concerns.has(value));
        button.setAttribute("aria-pressed", String(concerns.has(value)));
        experience.querySelector('[data-anou-formula-continue="1"]').disabled = concerns.size === 0;
      });
    });
    experience.querySelectorAll("[data-anou-formula-intent]").forEach((button) => {
      button.addEventListener("click", () => {
        intent = button.getAttribute("data-anou-formula-intent");
        experience.querySelectorAll("[data-anou-formula-intent]").forEach((item) => {
          const selected = item === button;
          item.classList.toggle("is-selected", selected);
          item.setAttribute("aria-pressed", String(selected));
        });
        experience.querySelector('[data-anou-formula-continue="2"]').disabled = false;
      });
    });
    experience.querySelectorAll("[data-anou-formula-daypart]").forEach((button) => {
      button.addEventListener("click", () => {
        dayPart = button.getAttribute("data-anou-formula-daypart");
        experience.querySelectorAll("[data-anou-formula-daypart]").forEach((item) => {
          const selected = item === button;
          item.classList.toggle("is-selected", selected);
          item.setAttribute("aria-pressed", String(selected));
        });
        experience.querySelector('[data-anou-formula-continue="3"]').disabled = false;
      });
    });
    experience.querySelector('[data-anou-formula-continue="1"]')?.addEventListener("click", () => showStep(2));
    experience.querySelector('[data-anou-formula-continue="2"]')?.addEventListener("click", () => intent === "routine" ? showResult() : showStep(3));
    experience.querySelector('[data-anou-formula-continue="3"]')?.addEventListener("click", showResult);
    back?.addEventListener("click", () => showStep(Math.max(1, activeStep - 1)));
    experience.querySelector("[data-anou-formula-reset]")?.addEventListener("click", reset);
  }

  function setupMenus(root) {
    const panel = root.querySelector(".soft-left-panel");
    const topbar = root.querySelector(".soft-mobile-topbar");
    const mobileMenu = root.querySelector("[data-anou-mobile-menu]");
    const mobileToggle = root.querySelector("[data-anou-menu-toggle]");
    const leftMenu = root.querySelector("[data-anou-left-menu]");
    const railToggle = root.querySelector("[data-anou-rail-toggle]");
    let railPinned = false;
    let railCloseTimer = 0;

    const clearRailClose = () => {
      if (!railCloseTimer) return;
      clearTimeout(railCloseTimer);
      railCloseTimer = 0;
    };
    const scheduleRailClose = () => {
      if (railPinned) return;
      clearRailClose();
      railCloseTimer = setTimeout(() => {
        setRailOpen(false);
        railCloseTimer = 0;
      }, 650);
    };

    const setMobileOpen = (open) => {
      if (!mobileMenu || !mobileToggle) return;
      mobileToggle.setAttribute("aria-expanded", String(open));
      mobileToggle.setAttribute("aria-label", open ? "Close navigation" : "Open navigation");
      mobileMenu.setAttribute("aria-hidden", String(!open));
      mobileMenu.toggleAttribute("inert", !open);
      topbar?.classList.toggle("is-menu-open", open);
      panel?.classList.toggle("is-menu-open", open);
      root.querySelectorAll(".soft-rail-quiz, .soft-mobile-whatsapp-launcher, .soft-mobile-formula-cta-fixed").forEach((action) => {
        action.setAttribute("data-menu-open", String(open));
      });
    };
    const setRailOpen = (open) => {
      if (open) clearRailClose();
      leftMenu?.classList.toggle("is-menu-open", open);
      railToggle?.setAttribute("aria-expanded", String(open));
    };

    mobileToggle?.addEventListener("click", () => setMobileOpen(mobileToggle.getAttribute("aria-expanded") !== "true"));
    mobileMenu?.querySelectorAll("[data-anou-menu-close], a").forEach((item) => item.addEventListener("click", () => setMobileOpen(false)));
    railToggle?.addEventListener("click", () => {
      const shouldClose = railPinned && railToggle.getAttribute("aria-expanded") === "true";
      railPinned = !shouldClose;
      setRailOpen(!shouldClose);
    });
    leftMenu?.addEventListener("mouseenter", () => setRailOpen(true));
    leftMenu?.addEventListener("mouseleave", scheduleRailClose);
    leftMenu?.addEventListener("focusin", () => setRailOpen(true));
    leftMenu?.addEventListener("focusout", (event) => {
      if (!leftMenu.contains(event.relatedTarget)) scheduleRailClose();
    });
    leftMenu?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => {
      clearRailClose();
      railPinned = false;
      setRailOpen(false);
    }));
  }

  function setupMobileTopbar(root) {
    const topbar = root.querySelector(".soft-mobile-topbar");
    const panel = root.querySelector(".soft-left-panel");
    if (!topbar) return;

    const mobile = matchMedia("(max-width: 960px)");
    let hidden = false;
    let anchorY = Math.max(scrollY, 0);
    let frame = 0;
    let idleRevealTimer = 0;

    const setHidden = (nextHidden) => {
      hidden = nextHidden;
      topbar.classList.toggle("is-hidden", nextHidden);
      panel?.classList.toggle("is-topbar-hidden", nextHidden);
    };
    const clearIdleReveal = () => {
      if (!idleRevealTimer) return;
      window.clearTimeout(idleRevealTimer);
      idleRevealTimer = 0;
    };
    const scheduleIdleReveal = () => {
      clearIdleReveal();
      idleRevealTimer = window.setTimeout(() => {
        idleRevealTimer = 0;
        if (!mobile.matches || topbar.classList.contains("is-menu-open")) return;
        anchorY = Math.max(scrollY, 0);
        setHidden(false);
      }, 320);
    };
    const update = () => {
      frame = 0;
      clearIdleReveal();
      const currentY = Math.max(scrollY, 0);

      if (!mobile.matches || topbar.classList.contains("is-menu-open") || currentY <= 12) {
        anchorY = currentY;
        setHidden(false);
        return;
      }

      const distance = currentY - anchorY;
      if (hidden) {
        if (distance <= -44) {
          anchorY = currentY;
          setHidden(false);
        } else if (distance > 0) {
          anchorY = currentY;
        }
        scheduleIdleReveal();
        return;
      }

      if (distance >= 26 && currentY > 36) {
        anchorY = currentY;
        setHidden(true);
        scheduleIdleReveal();
      } else if (distance < 0) {
        anchorY = currentY;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const reset = () => {
      anchorY = Math.max(scrollY, 0);
      schedule();
    };

    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", reset);
    mobile.addEventListener("change", reset);
    root.querySelector("[data-anou-menu-toggle]")?.addEventListener("click", schedule);
    update();
  }

  function setupStandardHeader(root) {
    const trigger = root.querySelector("[data-anou-standard-menu-open]");
    const overlay = root.querySelector("[data-anou-standard-menu]");
    if (!trigger || !overlay) return;

    const setOpen = (open) => {
      overlay.hidden = !open;
      overlay.classList.toggle("is-open", open);
      trigger.classList.toggle("is-open", open);
      trigger.setAttribute("aria-expanded", String(open));
      document.body.style.overflow = open ? "hidden" : "";
    };

    trigger.addEventListener("click", () => setOpen(true));
    overlay.querySelectorAll("[data-anou-standard-menu-close]").forEach((button) => {
      button.addEventListener("click", () => setOpen(false));
    });
    overlay.querySelector("[data-anou-standard-menu-expand]")?.addEventListener("click", (event) => {
      overlay.classList.remove("is-panel");
      overlay.classList.add("is-fullscreen");
      event.currentTarget.hidden = true;
    });
    overlay.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => setOpen(false)));
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !overlay.hidden) setOpen(false);
    });
  }

  function setupStoryCarousel(root) {
    root.querySelectorAll("[data-anou-story-carousel]").forEach((carousel) => {
      const slides = Array.from(carousel.querySelectorAll("[data-anou-story-slide]"));
      const dots = Array.from(carousel.querySelectorAll("[data-anou-story-dot]"));
      if (slides.length < 2 || dots.length !== slides.length) return;
      let active = 0;
      let timer = 0;

      const select = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const selected = slideIndex === active;
          slide.classList.toggle("is-active", selected);
          slide.setAttribute("aria-hidden", String(!selected));
        });
        dots.forEach((dot, dotIndex) => {
          const selected = dotIndex === active;
          dot.classList.toggle("is-active", selected);
          dot.setAttribute("aria-pressed", String(selected));
        });
      };
      const start = () => {
        clearInterval(timer);
        if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
          timer = setInterval(() => select(active + 1), 5000);
        }
      };

      dots.forEach((dot, index) => dot.addEventListener("click", () => { select(index); start(); }));
      start();
    });
  }

  function setupHorizontalRails(root) {
    root.querySelectorAll("[data-anou-horizontal-section]").forEach((section) => {
      const track = section.querySelector("[data-anou-horizontal-track]");
      if (!track) return;
      const buttons = section.querySelectorAll("[data-anou-scroll]");
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        buttons.forEach((button) => {
          const direction = Number(button.getAttribute("data-anou-scroll"));
          button.disabled = direction < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max - 2;
        });
      };
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          track.scrollBy({ left: Number(button.getAttribute("data-anou-scroll")) * track.clientWidth * 0.76, behavior: "smooth" });
        });
      });
      track.addEventListener("scroll", update, { passive: true });
      update();
    });
  }

  function setupVibes(root) {
    const section = root.querySelector("[data-anou-vibes]");
    const lines = Array.from(section?.querySelectorAll(".soft-vibes-line") ?? []);
    if (!section || !lines.length) return;
    let frame = 0;
    let active = 0;
    let wheelLockUntil = 0;
    let wheelAccumulator = 0;
    let wheelDirection = 0;
    let wheelReleaseDirection = 0;
    let wheelGestureStepped = false;
    let wheelGestureResetTimer = 0;
    const setActive = (nextActive) => {
      active = nextActive;
      section.style.setProperty("--active-index", String(active));
      lines.forEach((line, index) => {
        line.classList.toggle("is-active", index === active);
        line.classList.toggle("is-before", index < active);
        line.classList.toggle("is-after", index > active);
        if (index === active) line.setAttribute("aria-current", "true");
        else line.removeAttribute("aria-current");
      });
    };
    const update = () => {
      frame = 0;
      const mobile = window.matchMedia("(max-width: 960px)").matches;
      if (performance.now() < wheelLockUntil) return;
      if (wheelReleaseDirection !== 0) {
        const sticky = section.querySelector(".soft-vibes-sticky");
        if (sticky) {
          const rect = section.getBoundingClientRect();
          const stickyTop = Number.parseFloat(window.getComputedStyle(sticky).top) || 0;
          const hasExited = wheelReleaseDirection > 0
            ? rect.bottom < window.innerHeight - 2
            : rect.top > stickyTop + 2;
          if (!hasExited) return;
        }
        wheelReleaseDirection = 0;
      }
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = mobile ? 0 : Math.min(0.999, Math.max(0, -rect.top / travel));
      const edgeHold = 0.08;
      const stagedProgress = Math.min(
        0.999,
        Math.max(0, (progress - edgeHold) / (1 - edgeHold * 2)),
      );
      setActive(Math.min(lines.length - 1, Math.floor(stagedProgress * lines.length)));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const handleWheel = (event) => {
      if (
        event.ctrlKey ||
        Math.abs(event.deltaY) < 0.5 ||
        window.matchMedia("(max-width: 960px)").matches ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) return;

      const sectionRect = section.getBoundingClientRect();
      const pointerIsInside =
        section.contains(event.target) ||
        (
          event.clientX >= sectionRect.left &&
          event.clientX <= sectionRect.right &&
          event.clientY >= sectionRect.top &&
          event.clientY <= sectionRect.bottom
        );
      if (!pointerIsInside) return;

      const sticky = section.querySelector(".soft-vibes-sticky");
      if (!sticky) return;
      const stickyTop = Number.parseFloat(window.getComputedStyle(sticky).top) || 0;
      const isPinned =
        sectionRect.top <= stickyTop + 2 &&
        sectionRect.bottom >= stickyTop + sticky.offsetHeight - 2;
      if (!isPinned) {
        wheelAccumulator = 0;
        wheelDirection = 0;
        wheelGestureStepped = false;
        return;
      }

      const direction = event.deltaY > 0 ? 1 : -1;
      if (wheelDirection !== 0 && wheelDirection !== direction) {
        wheelGestureStepped = false;
      }
      window.clearTimeout(wheelGestureResetTimer);
      wheelGestureResetTimer = window.setTimeout(() => {
        wheelAccumulator = 0;
        wheelDirection = 0;
        wheelGestureStepped = false;
        wheelGestureResetTimer = 0;
      }, 180);
      if (wheelReleaseDirection !== 0 && wheelReleaseDirection !== direction) {
        wheelReleaseDirection = 0;
      }
      if (wheelGestureStepped) {
        event.preventDefault();
        return;
      }
      const canStep = direction > 0 ? active < lines.length - 1 : active > 0;
      if (!canStep) {
        wheelReleaseDirection = direction;
        wheelAccumulator = 0;
        wheelDirection = 0;
        return;
      }

      event.preventDefault();
      const now = performance.now();
      if (now < wheelLockUntil) return;
      if (wheelDirection !== direction) {
        wheelAccumulator = 0;
        wheelDirection = direction;
      }
      wheelAccumulator += event.deltaY;
      if (Math.abs(wheelAccumulator) < 36) return;

      wheelAccumulator = 0;
      wheelLockUntil = now + 620;
      wheelGestureStepped = true;
      setActive(Math.min(lines.length - 1, Math.max(0, active + direction)));
    };
    window.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    update();
  }

  function setupGallery(root) {
    root.querySelectorAll("[data-anou-gallery]").forEach((gallery) => {
      const main = gallery.querySelector("[data-anou-main-image]");
      const buttons = Array.from(gallery.querySelectorAll("[data-anou-gallery-thumb]"));
      const mainButton = gallery.querySelector(".soft-hero-main-image-button, .anou-soft-product__main-media");
      if (!main || !buttons.length) return;
      let active = Math.max(0, buttons.findIndex((button) => button.classList.contains("is-active")));
      let startX = null;
      let suppressNextClick = false;
      let suppressClickTimer = 0;
      const show = (index) => {
        active = (index + buttons.length) % buttons.length;
        const button = buttons[active];
        const slotClass = Array.from(button.classList).find((name) => /^is-hero-slot-\d+$/.test(name));
        main.setAttribute("src", button.getAttribute("data-full") || "");
        main.setAttribute("srcset", button.getAttribute("data-full-srcset") || "");
        main.setAttribute("alt", button.getAttribute("data-alt") || "");
        if (mainButton) {
          Array.from(mainButton.classList)
            .filter((name) => /^is-hero-slot-\d+$/.test(name))
            .forEach((name) => mainButton.classList.remove(name));
          if (slotClass) mainButton.classList.add(slotClass);
        }
        buttons.forEach((item, itemIndex) => {
          item.classList.toggle("is-active", itemIndex === active);
          item.setAttribute("aria-pressed", String(itemIndex === active));
        });
      };
      buttons.forEach((button, index) => button.addEventListener("click", () => show(index)));
      mainButton?.addEventListener("click", () => {
        if (suppressNextClick) {
          suppressNextClick = false;
          clearTimeout(suppressClickTimer);
          return;
        }
        show(active + 1);
      });
      mainButton?.addEventListener("keydown", (event) => {
        if (event.key === "ArrowRight") show(active + 1);
        if (event.key === "ArrowLeft") show(active - 1);
      });
      mainButton?.addEventListener("pointerdown", (event) => { startX = event.clientX; });
      mainButton?.addEventListener("pointerup", (event) => {
        const initialX = startX;
        startX = null;
        if (initialX === null || Math.abs(event.clientX - initialX) < 42) return;
        suppressNextClick = true;
        clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => { suppressNextClick = false; }, 420);
        show(active + (event.clientX < initialX ? 1 : -1));
      });
      mainButton?.addEventListener("pointercancel", () => { startX = null; });
    });
  }

  function setupCartForms(root) {
    root.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const button = form.querySelector('[type="submit"]');
        if (button) button.disabled = true;
        try {
          const response = await fetch("/cart/add.js", { method: "POST", body: new FormData(form), headers: { "X-Requested-With": "XMLHttpRequest" } });
          if (!response.ok) throw new Error("Unable to add item");
          window.location.href = "/cart";
        } catch {
          form.submit();
        } finally {
          if (button) button.disabled = false;
        }
      }, true);
    });
  }

  function setupDiscountCode(root) {
    const input = root.querySelector("[data-anou-discount-code]");
    const button = root.querySelector("[data-anou-discount-apply]");
    const status = root.querySelector("[data-anou-discount-status]");
    const statusMessage = status?.querySelector("[data-anou-discount-message]");
    const removeButton = status?.querySelector("[data-anou-discount-remove]");
    if (!input || !button) return;
    const storageKey = "anou-track-b-discount-attempt";
    let attemptedCode = "";
    try {
      attemptedCode = window.sessionStorage.getItem(storageKey) || "";
      window.sessionStorage.removeItem(storageKey);
    } catch {
      attemptedCode = "";
    }
    if (attemptedCode && status) {
      const appliedCodes = (status.dataset.anouAppliedDiscounts || "")
        .split("|")
        .map((code) => code.trim().toLowerCase())
        .filter(Boolean);
      const applied = appliedCodes.includes(attemptedCode.toLowerCase());
      status.hidden = false;
      status.classList.toggle("is-paid", applied);
      status.classList.toggle("is-failed", !applied);
      if (statusMessage) statusMessage.textContent = applied
        ? `${attemptedCode} applied.`
        : `${attemptedCode} could not be applied.`;
      if (!applied) input.value = attemptedCode;
    }
    const cartUpdateUrl = `${window.Shopify?.routes?.root || "/"}cart/update.js`;
    const updateDiscount = async (code) => {
      const response = await fetch(cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ discount: code }),
      });
      if (!response.ok) throw new Error("Unable to update discount");
      return response.json();
    };
    const apply = async () => {
      const code = input.value.trim();
      if (!code) {
        input.focus();
        return;
      }
      button.disabled = true;
      try {
        window.sessionStorage.setItem(storageKey, code);
      } catch {
        // Shopify still validates the code when session storage is unavailable.
      }
      try {
        await updateDiscount(code);
        window.location.reload();
      } catch {
        if (status && statusMessage) {
          status.hidden = false;
          status.classList.remove("is-paid");
          status.classList.add("is-failed");
          statusMessage.textContent = "That discount could not be applied. Please try again.";
        }
        button.disabled = false;
      }
    };
    button.addEventListener("click", apply);
    input.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      apply();
    });
    removeButton?.addEventListener("click", async () => {
      removeButton.disabled = true;
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // The cart update remains authoritative when session storage is unavailable.
      }
      try {
        await updateDiscount("");
        window.location.reload();
      } catch {
        if (statusMessage) statusMessage.textContent = "The discount could not be removed. Please try again.";
        status?.classList.add("is-failed");
        removeButton.disabled = false;
      }
    });
  }

  function setupPurchaseChoices(root) {
    root.querySelectorAll("[data-anou-purchase-root]").forEach((purchaseRoot) => {
      const choices = purchaseRoot.querySelectorAll("[data-anou-purchase-choice]");
      const variantInput = purchaseRoot.querySelector("[data-anou-purchase-variant]");
      const price = purchaseRoot.querySelector("[data-anou-purchase-price]");
      const compare = purchaseRoot.querySelector("[data-anou-purchase-compare]");
      const cta = purchaseRoot.querySelector("[data-anou-purchase-cta]");
      const stickyVariant = root.querySelector("[data-anou-sticky-variant]");
      const stickyPrice = root.querySelector("[data-anou-sticky-price]");
      const stickyCta = root.querySelector("[data-anou-sticky-cta]");
      choices.forEach((choice) => {
        choice.addEventListener("click", () => {
          choices.forEach((item) => {
            const active = item === choice;
            item.classList.toggle("active", active);
            item.setAttribute("aria-checked", String(active));
          });
          if (variantInput) variantInput.value = choice.dataset.variantId || variantInput.value;
          if (price) price.textContent = choice.dataset.price || price.textContent;
          if (compare) {
            compare.textContent = choice.dataset.compareAt || "";
            compare.hidden = !choice.dataset.compareAt;
          }
          if (cta) {
            cta.classList.remove("is-aro", "is-dali", "is-duo");
            cta.classList.add(`is-${choice.dataset.slug || "duo"}`);
            cta.classList.toggle("soft-duo-buy-cta", choice.dataset.slug === "duo");
          }
          if (stickyVariant) stickyVariant.value = choice.dataset.variantId || stickyVariant.value;
          if (stickyPrice) stickyPrice.textContent = choice.dataset.price || stickyPrice.textContent;
          if (stickyCta) {
            stickyCta.classList.remove("is-aro", "is-dali", "is-duo", "is-carryall");
            stickyCta.classList.add(`is-${choice.dataset.slug || "duo"}`);
          }
        });
      });
    });
  }

  function setupStickyPdp(root) {
    const sticky = root.querySelector("[data-anou-sticky-pdp]");
    const hero = root.querySelector(".soft-product-hero-panel");
    if (!sticky || !hero) return;

    const mobile = matchMedia("(max-width: 960px)");
    let ready = false;
    let frame = 0;
    const setReady = (nextReady) => {
      ready = nextReady;
      sticky.setAttribute("data-visible", String(nextReady));
    };
    const update = () => {
      frame = 0;
      if (!mobile.matches) {
        setReady(false);
        return;
      }
      const currentY = Math.max(scrollY, 0);
      if (currentY <= 18) {
        setReady(false);
        return;
      }
      const viewportHeight = window.visualViewport?.height || innerHeight;
      const shouldShow =
        hero.getBoundingClientRect().bottom <= viewportHeight * 0.78 ||
        currentY > viewportHeight * 0.56;
      setReady(ready ? currentY > 18 : shouldShow);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    mobile.addEventListener("change", update);
    update();
  }

  function setupUsage(root) {
    const mobile = matchMedia("(max-width: 960px)");
    root.querySelectorAll(".soft-usage-guide").forEach((guide) => {
      const steps = Array.from(guide.querySelectorAll("[data-anou-usage-step]"));
      const panels = Array.from(guide.querySelectorAll("[data-anou-usage-panel]"));
      const flow = guide.querySelector(".soft-usage-flow");
      if (!steps.length) return;
      const show = (active) => {
        steps.forEach((step, index) => {
          const item = step.closest("li");
          item?.classList.toggle("is-active", index === active);
          item?.classList.toggle("is-before", index < active);
          item?.classList.toggle("is-after", index > active);
          step.setAttribute("aria-pressed", String(index === active));
        });
        panels.forEach((panel, index) => {
          const visible = index === Math.min(active, panels.length - 1);
          panel.classList.toggle("is-active", visible);
          panel.setAttribute("aria-hidden", String(!visible));
        });
      };
      steps.forEach((step, index) => step.addEventListener("click", () => show(index)));
      let flowFrame = 0;
      const syncStepInView = () => {
        flowFrame = 0;
        if (!mobile.matches || !flow) return;
        const flowRect = flow.getBoundingClientRect();
        const center = flowRect.left + flowRect.width / 2;
        const nearest = steps.reduce((best, step, index) => {
          const rect = step.closest("li")?.getBoundingClientRect() ?? step.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY });
        show(nearest.index);
      };
      const scheduleFlowSync = () => {
        if (!flowFrame) flowFrame = requestAnimationFrame(syncStepInView);
      };
      flow?.addEventListener("scroll", scheduleFlowSync, { passive: true });
      window.addEventListener("resize", scheduleFlowSync);
      mobile.addEventListener("change", scheduleFlowSync);
      show(0);
      scheduleFlowSync();
    });

    root.querySelectorAll(".soft-pdp-quadrant-board").forEach((board) => {
      const cards = Array.from(board.querySelectorAll(":scope > article"));
      if (!cards.length) return;
      let boardFrame = 0;
      const setActive = (active) => {
        cards.forEach((card, index) => {
          const isActive = index === active;
          card.classList.toggle("is-active", isActive);
          if (isActive) card.setAttribute("aria-current", "true");
          else card.removeAttribute("aria-current");
        });
      };
      const syncCardInView = () => {
        boardFrame = 0;
        if (!mobile.matches) return;
        const boardRect = board.getBoundingClientRect();
        const center = boardRect.left + boardRect.width / 2;
        const nearest = cards.reduce((best, card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY });
        setActive(nearest.index);
      };
      const scheduleCardSync = () => {
        if (!boardFrame) boardFrame = requestAnimationFrame(syncCardInView);
      };
      board.addEventListener("scroll", scheduleCardSync, { passive: true });
      window.addEventListener("resize", scheduleCardSync);
      mobile.addEventListener("change", scheduleCardSync);
      scheduleCardSync();
    });
  }

  function setupParityVideos(root) {
    root.querySelectorAll("[data-anou-parity-video]").forEach((frame) => {
      const video = frame.querySelector("video");
      if (!video) return;
      let activated = false;
      const activate = () => {
        if (activated) return;
        video.querySelectorAll("source[data-src]").forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute("data-src");
        });
        activated = true;
        video.load();
      };
      video.addEventListener("playing", () => frame.classList.add("is-video-playing"));
      video.addEventListener("pause", () => frame.classList.remove("is-video-playing"));
      video.addEventListener("error", () => frame.classList.remove("is-video-playing"));
      if (!("IntersectionObserver" in window)) {
        activate();
        video.play().catch(() => frame.classList.remove("is-video-playing"));
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            activate();
            video.play().catch(() => frame.classList.remove("is-video-playing"));
          } else {
            video.pause();
          }
        },
        { rootMargin: "160px" },
      );
      observer.observe(frame);
    });
  }

  function setupScrollSignal(root) {
    const signalRoot = root.querySelector(
      ".soft-home, .soft-product-page, .soft-story-page, .soft-info-page, .soft-formula-page, .checkout-page",
    ) || root;
    let frame = 0;

    const updateBounds = () => {
      const panel = signalRoot.querySelector(".soft-left-panel");
      const menu = panel?.querySelector(".soft-left-menu");
      const signal = panel?.querySelector(".soft-scroll-signal");
      if (!panel || !menu || !signal) return;

      const panelRect = panel.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const margin = Math.round(Math.min(Math.max(innerHeight * 0.018, 12), 20));
      const bottomAnchors = Array.from(
        signalRoot.querySelectorAll(".soft-rail-quiz, .soft-support-card, .soft-product-summary"),
      ).filter((node) => node.getBoundingClientRect().height > 0);
      const topAnchor = bottomAnchors.sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
      )[0];
      const top = Math.max(0, menuRect.bottom - panelRect.top + margin);
      const bottom = topAnchor
        ? Math.max(margin, panelRect.bottom - topAnchor.getBoundingClientRect().top + margin)
        : margin;

      panel.style.setProperty("--soft-scroll-signal-top", `${Math.round(top)}px`);
      panel.style.setProperty("--soft-scroll-signal-bottom", `${Math.round(bottom)}px`);
    };

    const update = () => {
      frame = 0;
      updateBounds();
      const max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      const progress = Math.min(1, Math.max(0, scrollY / max));
      const signal = signalRoot.querySelector(".soft-scroll-signal");
      const sigil = signalRoot.querySelector(".soft-scroll-sigil");
      const travel = Math.max(0, (signal?.clientHeight ?? 420) - (sigil?.clientHeight ?? 42));
      signalRoot.style.setProperty("--soft-scroll-marker-y", `${Math.round(progress * travel)}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);

    const panel = signalRoot.querySelector(".soft-left-panel");
    const resizeObserver = typeof ResizeObserver === "undefined" || !panel
      ? null
      : new ResizeObserver(schedule);
    if (resizeObserver && panel) {
      resizeObserver.observe(panel);
      panel.querySelectorAll(".soft-left-menu, .soft-rail-quiz, .soft-support-card")
        .forEach((node) => resizeObserver.observe(node));
    }
    update();
  }

  function setupReadSectionNavigation(root) {
    root.querySelectorAll(".soft-read-page").forEach((page) => {
      const guide = page.querySelector("[data-anou-section-guide]");
      if (!guide) return;
      const links = Array.from(guide.querySelectorAll("[data-section-id]"));
      const sectionIds = links.map((link) => link.getAttribute("data-section-id")).filter(Boolean);
      if (!sectionIds.length) return;

      let frame = 0;
      const activate = (sectionId) => {
        links.forEach((link) => {
          const active = link.getAttribute("data-section-id") === sectionId;
          link.classList.toggle("is-active", active);
          if (active) link.setAttribute("aria-current", "location");
          else link.removeAttribute("aria-current");
        });

        const activeLink = links.find((link) => link.getAttribute("data-section-id") === sectionId);
        if (!activeLink || guide.scrollWidth <= guide.clientWidth) return;
        const left = activeLink.offsetLeft - (guide.clientWidth - activeLink.offsetWidth) / 2;
        const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
        guide.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? "auto" : "smooth" });
      };
      const update = () => {
        frame = 0;
        const activationLine = innerWidth <= 960 ? 142 : 112;
        let activeSectionId = sectionIds[0];
        sectionIds.some((sectionId) => {
          const section = document.getElementById(sectionId);
          if (!section) return false;
          if (section.getBoundingClientRect().top > activationLine) return true;
          activeSectionId = sectionId;
          return false;
        });
        activate(activeSectionId);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      const syncHash = () => {
        const hashSectionId = decodeURIComponent(location.hash.slice(1));
        if (sectionIds.includes(hashSectionId)) activate(hashSectionId);
        schedule();
      };

      links.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          const sectionId = link.getAttribute("data-section-id");
          const section = document.getElementById(sectionId);
          if (!section) return;
          const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
          activate(sectionId);
          history.pushState(null, "", `#${sectionId}`);
          section.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
        });
      });
      addEventListener("scroll", schedule, { passive: true });
      addEventListener("resize", schedule);
      addEventListener("hashchange", syncHash);
      document.addEventListener("scroll", schedule, { capture: true, passive: true });
      update();
      page.setAttribute("data-section-nav-ready", "true");
    });
  }
})();
